import React, { useState } from 'react';
import useSWR from 'swr';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Loader2 } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import ImportTypeCard from '@/components/estimating/ImportTypeCard';
import ImportDialog from '@/components/estimating/ImportDialog';
import FinancialSummary from '@/components/estimating/FinancialSummary';
import ReferenceDocs from '@/components/estimating/ReferenceDocs';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { listImports } from '@/lib/estimating/service';
import { IMPORT_TYPE_ORDER } from '@/lib/estimating/templates';
import { EST_IDS } from '@/constants/testIds/estimating';

async function fetchProject([_, id, organisationId]) {
  let q = supabase.from('projects').select('*').eq('id', id);
  if (organisationId) q = q.eq('organisation_id', organisationId);
  const { data, error } = await q.maybeSingle();
  if (error) throw error;
  return data;
}

export default function ProjectEstimatingPage() {
  const { id: projectId } = useParams();
  const { organisationId, role } = useAuth();
  const canImport = ['owner', 'admin'].includes(role || '');
  const canSeeCost = ['owner', 'admin', 'manager'].includes(role || '');
  const [dialogType, setDialogType] = useState(null);

  const { data: project, error: projectError, isLoading: projectLoading } = useSWR(projectId ? ['project', projectId, organisationId] : null, fetchProject);
  const { data: imports, error: importsError, isLoading: importsLoading, mutate } = useSWR(projectId ? ['estimate-imports', projectId] : null, () => listImports(projectId));

  const byType = (t) => (imports || []).filter((i) => i.import_type === t);
  const confirmedOf = (t) => byType(t).find((i) => i.status === 'confirmed');
  const tender = confirmedOf('tender_summary');

  return (
    <div data-testid={EST_IDS.estimatingPage} className="p-6 md:p-8 max-w-7xl mx-auto">
      <Link to={`/projects/${projectId}`} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-charcoal mb-4 transition-colors duration-150">
        <ArrowLeft size={14} /> Back to project
      </Link>
      <PageHeader
        title="Estimating & BOQ"
        subtitle={project ? `${project.name || 'Project'} · ${project.project_code || ''}` : 'Project estimating'}
      />

      {(projectError || importsError) && (
        <div className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-start gap-2">
          <AlertCircle size={16} className="mt-0.5" />
          <div>
            {(projectError || importsError).message}
            {/relation|does not exist|schema cache/i.test((projectError || importsError).message || '') && (
              <div className="mt-1 text-xs">The estimating tables are not present yet — run <code>estimating_schema_migration.sql</code> in Supabase.</div>
            )}
          </div>
        </div>
      )}

      {(projectLoading || importsLoading) ? (
        <div className="py-16 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 size={16} className="animate-spin" /> Loading estimating data…</div>
      ) : (
        <>
          <FinancialSummary tender={tender} boq={confirmedOf('priced_boq')} cost={confirmedOf('cost_budget')} canSeeCost={canSeeCost} />

          <div className="mb-3 rounded-md border border-border bg-muted/30 px-4 py-3 text-xs text-charcoal">
            <span className="font-medium">How importing works:</span> download a template → fill it in Excel (row 1 headings, data from row 2, blank rows ignored) → import. Every file is validated for quantities, rates, totals and cost codes before anything is saved. New imports create a new version; confirming supersedes the previous confirmed version and keeps history. Import the <strong>Tender Summary</strong> first so the Priced BOQ reconciles to it automatically.
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
            {IMPORT_TYPE_ORDER.map((t) => (
              <ImportTypeCard key={t} type={t} imports={byType(t)} projectId={projectId}
                canImport={canImport} canView={t !== 'cost_budget' || canSeeCost} onImport={setDialogType} />
            ))}
          </div>

          <ReferenceDocs projectId={projectId} confirmedTenderId={tender?.id} />
        </>
      )}

      {dialogType && project && (
        <ImportDialog open={!!dialogType} onOpenChange={(v) => !v && setDialogType(null)} project={project}
          importType={dialogType} tenderSummary={tender} onDone={() => mutate()} />
      )}
    </div>
  );
}
